import React, {
	type SyntheticEvent,
	type FunctionComponent,
	type ChangeEvent,
	type ReactNode,
	useMemo,
	useCallback,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Field, ErrorMessage } from '@atlaskit/form';
import CheckIcon from '@atlaskit/icon/glyph/check';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import Select, { type ControlProps, components, type OptionProps } from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import ProjectAccess from '@atlassian/jira-project-access/src';
import type {
	FREE_ACCESS_LEVEL,
	OPEN_ACCESS_LEVEL,
	AccessLevel,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import CompanyManagedIcon from '../../../common/assets/CompanyManaged.svg';
import TeamManagedIcon from '../../../common/assets/TeamManaged.svg';
import { CONTACT_ADMIN_LINK, PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID } from '../../../common/constants';
import {
	type ProjectStyle,
	PROJECT_KEY_LEARN_MORE_LINK,
	ProjectStyles,
} from '../../../common/constants/project';
import { Accordion } from '../../../common/ui/accordion';
import { InfoButton } from '../../../common/ui/info-button';
import { useProjectSpecificContent } from '../../../common/utils/project-specific-content';
import {
	useMappingState,
	useProjectData,
	useThirdPartyState,
} from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors/source';
import messages from './messages';

const dropdownWidthStyles = {
	minWidth: '400px',
	maxWidth: '528px',
};

export type Props = {
	projectKey: string;
	onProjectKeyChange: (e: ChangeEvent<HTMLInputElement>) => void;
	projectKeyValidationError?: string;
	isTMPSupported?: boolean;
	isPermissionGated: boolean;
	defaultProjectAccess: typeof FREE_ACCESS_LEVEL | typeof OPEN_ACCESS_LEVEL;
	disableProjectStyle?: boolean;
};

export type OptionType = {
	label: string;
	value: ProjectStyle;
	icon: ReactNode;
	description: string;
	lozengeContent: ReactNode;
	disabledContent: ReactNode;
	disabledTooltipContent?: string;
	onLearnMoreButtonClick?: (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	learnMoreLink?: string;
	isDisabled: boolean;
};

const CustomOption: FunctionComponent<OptionProps<OptionType>> = (props) => {
	const {
		isDisabled,
		label,
		lozengeContent,
		description,
		icon,
		disabledTooltipContent,
		disabledContent,
	} = props.data;

	return (
		<components.Option {...props}>
			<ConditionalWrapper
				condition={isDisabled}
				renderWrapper={(children) => (
					<Tooltip content={disabledTooltipContent || ''}>{children}</Tooltip>
				)}
			>
				<OptionWrapper isDisabled={isDisabled}>
					<Box xcss={iconWrapperStyles}>{icon}</Box>
					<Box xcss={contentWrapperStyles}>
						<TitleWrapper>
							{label}
							{lozengeContent && <LozengeWrapper>{lozengeContent}</LozengeWrapper>}
						</TitleWrapper>
						<DescriptionWrapper>{description}</DescriptionWrapper>
					</Box>
					{props.isSelected && (
						<CheckIcon primaryColor={token('color.icon.information', colors.B200)} label="" />
					)}
				</OptionWrapper>
			</ConditionalWrapper>
			{isDisabled && <DisabledContentWrapper>{disabledContent}</DisabledContentWrapper>}
		</components.Option>
	);
};

const CustomControl: FunctionComponent<ControlProps<OptionType>> = ({ children, ...props }) => {
	const selectedOption = props.getValue();
	const selectedIcon = selectedOption && selectedOption[0] != null ? selectedOption[0].icon : null;

	return (
		<components.Control {...props}>
			<IconControlWrapper>{selectedIcon}</IconControlWrapper>
			{children}
		</components.Control>
	);
};

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const ProjectCreateMoreOptions = ({
	projectKey,
	onProjectKeyChange,
	projectKeyValidationError,
	isTMPSupported,
	isPermissionGated,
	defaultProjectAccess,
	disableProjectStyle,
}: Props) => {
	const { formatMessage } = useIntl();
	const [mappingState, { setMappingState }] = useMappingState();
	const [{ thirdPartyFieldConfigState }, { setThirdPartyState }] = useThirdPartyState();
	const [{ type, accessLevel, style }, { setProjectData }] = useProjectData();
	const { isSiteAdmin } = useTenantContext();
	const [source] = useSource();
	const { projectCreateMoreOptions, projectCreateHideMoreOptions } = useProjectSpecificContent(
		type,
		source,
	);

	const companyManagedOption = useMemo(
		() => ({
			label: formatMessage(messages.companyManagedLabel),
			value: ProjectStyles.COMPANY_MANAGED_PROJECT,
			icon: <Image src={CompanyManagedIcon} aria-label="company managed" role="img" alt="" />,
			lozengeContent: (
				<Lozenge appearance="inprogress">{formatMessage(messages.adminOnlyLozenge)}</Lozenge>
			),
			description: formatMessage(messages.companyManagedDescription),
			disabledContent: (
				<a target="_blank" href={CONTACT_ADMIN_LINK}>
					{formatMessage(messages.contactAdminCta)}
				</a>
			),
			disabledTooltipContent: formatMessage(
				messages.cannotCreateCompanyManagedProjectTooltipMessage,
			),
			isDisabled: ff('gun-2207-non-admin-support-in-tmp-migration_k7z9p') ? !isSiteAdmin : false,
		}),
		[formatMessage, isSiteAdmin],
	);

	const teamManagedOption = useMemo(
		() => ({
			label: formatMessage(messages.teamManagedLabel),
			value: ProjectStyles.TEAM_MANAGED_PROJECT,
			icon: <Image src={TeamManagedIcon} aria-label="team managed" role="img" alt="" />,
			lozengeContent: null,
			description: formatMessage(messages.teamManagedDescription),
			disabledContent: null,
			disabledTooltipContent: formatMessage(messages.cannotCreateTeamManagedProjectTooltipMessage),
			isDisabled: !isTMPSupported,
		}),
		[formatMessage, isTMPSupported],
	);

	const OPTIONS = useMemo(
		() => [teamManagedOption, companyManagedOption],
		[companyManagedOption, teamManagedOption],
	);

	const valueForProjectStyle = useMemo(
		() => OPTIONS.find((item) => item.value === style),
		[OPTIONS, style],
	);

	const showProjectAccessField =
		isTMPSupported && valueForProjectStyle?.value === ProjectStyles.TEAM_MANAGED_PROJECT;

	const projectKeyFieldLabel = useMemo(
		() => (
			<LabelWrapper data-testid="project-create-importer-upload-flow.ui.project-create-screen.project-create-more-options.label-wrapper">
				{formatMessage(messages.projectKey)}
				<InfoButton
					description={
						<FormattedMessage
							values={{
								learnMoreLink: (
									<a href={PROJECT_KEY_LEARN_MORE_LINK} target="_blank">
										{formatMessage(messages.learnMoreProjectKey)}
									</a>
								),
							}}
							{...messages.projectKeyDescription}
						/>
					}
				/>
			</LabelWrapper>
		),
		[formatMessage],
	);

	const onChangeProjectStyle = useCallback(
		(selectedProjectStyle: OptionType | null) => {
			if (!selectedProjectStyle?.value) {
				return;
			}

			// Reset mapping state if data has previously been loaded
			if (mappingState !== null) {
				setMappingState(null);
			}

			if (thirdPartyFieldConfigState !== null) {
				setThirdPartyState({
					thirdPartyFieldConfigState: null,
					thirdPartyStatusMapping: null,
				});
			}

			if (isTMPSupported) {
				const projectAccessLevel =
					selectedProjectStyle.value === ProjectStyles.COMPANY_MANAGED_PROJECT
						? null
						: defaultProjectAccess;
				setProjectData({
					style: selectedProjectStyle.value,
					accessLevel: projectAccessLevel,
				});
				return;
			}
			setProjectData({
				style: selectedProjectStyle.value,
			});
		},
		[
			defaultProjectAccess,
			isTMPSupported,
			mappingState,
			setMappingState,
			setProjectData,
			setThirdPartyState,
			thirdPartyFieldConfigState,
		],
	);

	const onProjectAccessChange = useCallback(
		(value: AccessLevel) => {
			setProjectData({ accessLevel: value });
		},
		[setProjectData],
	);

	return (
		<Wrapper>
			<Accordion
				shouldForceOpen={projectKeyValidationError !== ''}
				heading={projectCreateMoreOptions}
				expandedHeading={projectCreateHideMoreOptions}
				testId="project-create-importer-upload-flow.ui.project-create-screen.project-create-more-options.accordion"
			>
				<MoreOptionWrapper>
					<Field
						label={formatMessage(messages.projectType)}
						name="projectType"
						isRequired
						isDisabled={disableProjectStyle}
					>
						{({ fieldProps }) => (
							<Select<OptionType>
								{...fieldProps}
								inputId={PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID}
								name={PROJECT_MANAGEMENT_TYPE_DROPDOWN_ID}
								options={OPTIONS}
								value={valueForProjectStyle}
								onChange={onChangeProjectStyle}
								components={{
									Option: CustomOption,
									Control: CustomControl,
								}}
							/>
						)}
					</Field>
					{showProjectAccessField && type && (
						<ProjectAccessWrapper>
							<ProjectAccess
								projectType={type}
								value={accessLevel || undefined}
								onChange={onProjectAccessChange}
								onFirstChange={noop}
								onAccessTypesLoaded={noop}
								onError={noop}
								validateRequired={false}
								requiredAccess={false}
								isDisabled={isPermissionGated}
								dropdownWidthStyles={dropdownWidthStyles}
								baseUrl=""
							/>
						</ProjectAccessWrapper>
					)}
					<ProjectKeyWrapper>
						<Field label={projectKeyFieldLabel} name="key" isRequired>
							{({ fieldProps }) => (
								<>
									<TextField
										testId="project-create-importer-upload-flow.ui.project-create-screen.project-create-more-options.project-key-field"
										{...fieldProps}
										value={projectKey}
										onChange={onProjectKeyChange}
									/>
									{projectKeyValidationError !== '' && (
										<ErrorMessage>{projectKeyValidationError}</ErrorMessage>
									)}
								</>
							)}
						</Field>
					</ProjectKeyWrapper>
				</MoreOptionWrapper>
			</Accordion>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	marginTop: token('space.200', '16px'),
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.div<{ isDisabled?: boolean }>(
	{
		display: 'flex',
		padding: token('space.100', '8px'),
		paddingBottom: token('space.200', '16px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: ({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDisabled }) => (isDisabled ? 'opacity: 0.5;' : ''),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MoreOptionWrapper = styled.div({
	flexDirection: 'row',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	label: {
		display: 'flex',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectKeyWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	marginTop: token('space.200', '16px'),
	width: '168px',
});

const iconWrapperStyles = xcss({
	width: 'space.300',
	height: 'space.300',
	marginRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisabledContentWrapper = styled.div({
	font: token('font.body.small'),
	marginLeft: token('space.500', '40px'),
	marginTop: token('space.negative.100', '-8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconControlWrapper = styled.div({
	width: '24px',
	height: '24px',
	marginLeft: token('space.100', '8px'),
});

const contentWrapperStyles = xcss({
	marginRight: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.div({
	font: token('font.body'),
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionWrapper = styled.p({
	font: token('font.body.small'),
	marginTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		marginLeft: token('space.050', '4px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectAccessWrapper = styled.div({
	marginTop: `${token('space.200', '16px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> div:first-of-type': {
		width: '100%',
	},
});
